"use client"

import { useEffect, useRef, useState, type ReactNode } from "react"
import confetti from "canvas-confetti"
import { TrendingUp, type LucideIcon } from "lucide-react"
import {
  Label,
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  RadialBar,
  RadialBarChart,
} from "recharts"

import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { ChartConfig, ChartContainer } from "@/components/ui/chart"
import { cn } from "@/lib/utils"

export const description = "A radial chart with text"

type ChartRadialTextVariant = "card" | "minimal"

interface ChartRadialTextProps {
  value: number
  max?: number
  title?: string
  description?: string
  valueFormatter?: (value: number, percent: number) => string
  centerLabel?: string
  centerSecondaryLabel?: string
  color?: string
  footerLabel?: string
  footerDescription?: string
  footerIcon?: LucideIcon | null
  footerContent?: ReactNode
  className?: string
  chartClassName?: string
  variant?: ChartRadialTextVariant
  startAngle?: number
  endAngle?: number
  showRing?: boolean
}

const defaultValueFormatter = (_value: number, percent: number) =>
  `${Math.round(percent)}%`

export function ChartRadialText({
  value,
  max = 100,
  title,
  description,
  valueFormatter = defaultValueFormatter,
  centerLabel,
  centerSecondaryLabel,
  color,
  footerLabel,
  footerDescription,
  footerIcon = TrendingUp,
  footerContent,
  className,
  chartClassName,
  variant = "card",
  startAngle = 0,
  endAngle = 250,
  showRing = true,
}: ChartRadialTextProps) {
  const safeMax = Number.isFinite(max) && max > 0 ? max : 100
  const clampedValue = Math.min(Math.max(value, 0), safeMax)
  const percent = (clampedValue / safeMax) * 100
  const formattedValue = valueFormatter(clampedValue, percent)

  const resolveRingColor = (pct: number): string => {
    if (color) {
      return color
    }
    if (pct >= 80) {
      return "#10b981" // emerald-500
    }
    if (pct >= 40) {
      return "#f59e0b" // amber-500
    }
    return "#ef4444" // red-500
  }

  const ringColor = resolveRingColor(percent)
  const hasCelebratedRef = useRef(false)

  const chartData = [
    { name: "score", score: percent, fill: ringColor },
  ]

  const chartConfig = {
    score: {
      label: centerLabel ?? "Score",
      color: ringColor,
    },
  } satisfies ChartConfig

  const isMinimal = variant === "minimal"
  const valueFontClass = isMinimal ? "text-[15px] leading-tight" : "text-4xl"
  const labelFontClass = isMinimal ? "text-[10px]" : "text-base"
  const secondaryFontClass = isMinimal ? "text-[8px]" : "text-sm"

  const minimalStrokeWidth = 6
  const minimalRadius = 24 - minimalStrokeWidth / 2
  const minimalCircumference = 2 * Math.PI * minimalRadius
  const minimalDashOffset = minimalCircumference * (1 - percent / 100)
  const [animatedDashOffset, setAnimatedDashOffset] = useState<number>(
    minimalCircumference,
  )

  useEffect(() => {
    if (!showRing) {
      hasCelebratedRef.current = false
      return
    }

    if (percent >= 90 && !hasCelebratedRef.current) {
      if (typeof window !== "undefined") {
        confetti({
          spread: 60,
          startVelocity: 35,
          scalar: 0.7,
          particleCount: 120,
          origin: { y: 0.4 },
        })
      }
      hasCelebratedRef.current = true
    } else if (percent < 80) {
      hasCelebratedRef.current = false
    }
  }, [percent, showRing])

  useEffect(() => {
    if (!isMinimal || !showRing) {
      setAnimatedDashOffset(minimalCircumference)
      return
    }

    if (percent <= 0) {
      setAnimatedDashOffset(minimalCircumference)
      return
    }

    setAnimatedDashOffset((prev) => {
      if (Math.abs(prev - minimalDashOffset) < 0.5) {
        return minimalDashOffset
      }
      return prev
    })

    const frame = requestAnimationFrame(() => {
      setAnimatedDashOffset(minimalDashOffset)
    })

    return () => cancelAnimationFrame(frame)
  }, [
    isMinimal,
    showRing,
    minimalCircumference,
    minimalDashOffset,
    percent,
  ])

  const renderTextContent = () => (
    <>
      <span className={cn("font-bold -mb-1 text-foreground", valueFontClass)}>
        {formattedValue}
      </span>
      {centerLabel ? (
        <span
          className={cn(
            "text-muted-foreground",
            labelFontClass,
            isMinimal ? "mt-0" : "mt-1",
          )}
        >
          {centerLabel}
        </span>
      ) : null}
      {centerSecondaryLabel ? (
        <span className={cn("text-muted-foreground", secondaryFontClass)}>
          {centerSecondaryLabel}
        </span>
      ) : null}
    </>
  )

  if (isMinimal) {
    if (!showRing) {
      return (
        <div className={cn("flex flex-col items-center gap-1 text-center", className)}>
          {title ? (
            <span className="text-sm font-medium text-foreground">{title}</span>
          ) : null}
          <div className="flex flex-col items-center gap-0.25">
            {renderTextContent()}
          </div>
          {description ? (
            <span className="text-xs text-muted-foreground">{description}</span>
          ) : null}
        </div>
      )
    }

    const backgroundStroke = "rgba(148, 163, 184, 0.35)"

    return (
      <div className={cn("flex flex-col items-center gap-1", className)}>
        {title ? (
          <span className="text-sm font-medium text-foreground">{title}</span>
        ) : null}
        <div className={cn("relative h-14 w-14", chartClassName)}>
          <svg viewBox="0 0 56 56" className="h-full w-full">
            <circle
              cx="28"
              cy="28"
              r={minimalRadius}
              fill="none"
              stroke={backgroundStroke}
              strokeWidth={minimalStrokeWidth}
            />
            {percent > 0 ? (
              <circle
                cx="28"
                cy="28"
                r={minimalRadius}
                fill="none"
                stroke={ringColor}
                strokeWidth={minimalStrokeWidth}
                strokeDasharray={minimalCircumference}
                strokeDashoffset={animatedDashOffset}
                strokeLinecap="round"
                transform="rotate(-90 28 28)"
                style={{
                  transition: "stroke-dashoffset 0.45s ease, stroke 0.3s ease",
                }}
              />
            ) : null}
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-0 text-center">
            {renderTextContent()}
          </div>
        </div>
        {description ? (
          <span className="text-xs text-muted-foreground text-center">
            {description}
          </span>
        ) : null}
      </div>
    )
  }

  const outerRadius = 110
  const innerRadius = 80
  const polarRadii = [86, 74]
  const labelYOffset = 24
  const secondaryYOffset = 40
  const chartContainerClass = cn(
    "mx-auto aspect-square max-h-[250px]",
    chartClassName,
  )

  const polarGridClass = showRing
    ? "first:fill-muted last:fill-background"
    : "first:fill-transparent last:fill-transparent"
  const chartElement = (
    <ChartContainer config={chartConfig} className={chartContainerClass}>
      <RadialBarChart
        data={chartData}
        startAngle={startAngle}
        endAngle={endAngle}
        innerRadius={innerRadius}
        outerRadius={outerRadius}
      >
        <PolarAngleAxis
          type="number"
          domain={[0, safeMax]}
          tick={false}
          angleAxisId={0}
        />
        <PolarGrid
          gridType="circle"
          radialLines={false}
          stroke="none"
          className={polarGridClass}
          polarRadius={polarRadii}
        />
        <RadialBar dataKey="score" background cornerRadius={10} />
        <PolarRadiusAxis tick={false} tickLine={false} axisLine={false}>
          <Label
            content={({ viewBox }) => {
              if (viewBox && "cx" in viewBox && "cy" in viewBox) {
                return (
                  <text
                    x={viewBox.cx}
                    y={viewBox.cy}
                    textAnchor="middle"
                    dominantBaseline="middle"
                  >
                    <tspan
                      x={viewBox.cx}
                      y={viewBox.cy}
                      className={cn("fill-foreground font-bold", valueFontClass)}
                    >
                      {formattedValue}
                    </tspan>
                    {centerLabel ? (
                      <tspan
                        x={viewBox.cx}
                        y={(viewBox.cy || 0) + labelYOffset}
                        className={cn("fill-muted-foreground", labelFontClass)}
                      >
                        {centerLabel}
                      </tspan>
                    ) : null}
                    {centerSecondaryLabel ? (
                      <tspan
                        x={viewBox.cx}
                        y={(viewBox.cy || 0) + secondaryYOffset}
                        className={cn(
                          "fill-muted-foreground",
                          secondaryFontClass,
                        )}
                      >
                        {centerSecondaryLabel}
                      </tspan>
                    ) : null}
                  </text>
                )
              }
              return null
            }}
          />
        </PolarRadiusAxis>
      </RadialBarChart>
    </ChartContainer>
  )

  const shouldRenderFooter =
    Boolean(footerLabel || footerDescription) || footerContent

  const FooterIcon = footerIcon ?? null

  return (
    <Card className={cn("flex flex-col", className)}>
      {title || description ? (
        <CardHeader className="items-center pb-0 text-center">
          {title ? <CardTitle>{title}</CardTitle> : null}
          {description ? (
            <CardDescription>{description}</CardDescription>
          ) : null}
        </CardHeader>
      ) : null}
      <CardContent className="flex-1 pb-0">{chartElement}</CardContent>
      {shouldRenderFooter ? (
        <CardFooter className="flex-col gap-2 text-sm">
          {footerContent ? (
            footerContent
          ) : (
            <>
              {footerLabel ? (
                <div className="flex items-center gap-2 leading-none font-medium">
                  {footerLabel}
                  {FooterIcon ? (
                    <FooterIcon className="h-4 w-4" />
                  ) : null}
                </div>
              ) : null}
              {footerDescription ? (
                <div className="text-muted-foreground leading-none text-center">
                  {footerDescription}
                </div>
              ) : null}
            </>
          )}
        </CardFooter>
      ) : null}
    </Card>
  )
}
